import { z } from "zod/v4";
import { endpoint } from "@uri/typed-api";
import { aiQuery } from "./src/aiQuerySchema.ts";
import { eventEntrySchema } from "./src/fields.ts";

export const publicEventEntrySchema = eventEntrySchema.omit({
  apiSubmit: true,
  userSubmit: true,
});

export const eventCategoryChipSchema = z.object({
  emoji: z.string(),
  label: z.string(),
});

export const eventSourceBadgeSchema = z.object({
  kind: z.enum([
    "instagram",
    "whatsapp",
    "web",
    "user",
    "other",
  ]),
  label: z.string(),
  url: z.string().optional(),
  originalText: z.string().optional(),
});

export const eventPageViewSchema = z.object({
  dayName: z.string(),
  dateText: z.string(),
  timeText: z.string(),
  priceText: z.string().optional(),
  locationText: z.string(),
  city: z.string().optional(),
  mapsUrl: z.string().optional(),
  categories: z.array(eventCategoryChipSchema),
  calendarUrl: z.string().optional(),
  source: eventSourceBadgeSchema.optional(),
  isSoldOut: z.boolean().optional(),
  accessibilityText: z.string().optional(),
  isMultiDay: z.boolean().optional(),
  endDayName: z.string().optional(),
  endDateText: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
});

export const eventPageDataSchema = z.object({
  language: z.enum(["en", "he"]),
  event: publicEventEntrySchema,
  view: eventPageViewSchema,
});

const tasteDecisionSchema = z.object({
  event: z.string(),
  decision: z.string(),
  timestamp: z.number(),
});

const campaignTargetingSchema = {
  area: z.string().optional().describe(
    "Target geographic area or city, e.g. 'Tel Aviv', 'Haifa', 'Israel'",
  ),
  category: z.string().optional().describe(
    "Target category of interest, e.g. 'techno', 'party', 'standup'",
  ),
  gender: z.string().optional().describe(
    "Target gender for the audience: 'female' or 'male'",
  ),
  minAge: z.number().int().positive().optional(),
  maxAge: z.number().int().positive().optional(),
};

export const campaignOutcomeSchema = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    sentCount: z.number(),
    cost: z.number(),
    remainingBalance: z.number().optional(),
    message: z.string(),
  }),
  z.object({ ok: z.literal(false), message: z.string() }),
]);

export const userSubscriptionSchema = z.object({
  id: z.string(),
  phone: z.string(),
  country: z.string(),
  categories: z.array(z.string()),
  blacklistedCategories: z.array(z.string()).optional(),
  location: z.string().optional(),
  radiusKm: z.number().optional(),
  pushRRule: z.string().optional(),
  pushLocalHour: z.number().optional(),
  pushLocalMinute: z.number().optional(),
  instant: z.boolean().optional(),
  ticketAlerts: z.string().optional(),
  minTasteMatch: z.number().optional(),
  maxEvents: z.number().optional(),
  blacklistedWords: z.array(z.string()).optional(),
  whitelistedWords: z.array(z.string()).optional(),
  conversationId: z.string().optional(),
  waitingEventIds: z.array(z.string()).optional(),
  lastAggregatedPushAt: z.number().optional(),
});

export const upsertUserSubscriptionInputSchema = z.object({
  id: z.string().optional(),
  country: z.string(),
  categories: z.array(z.string()),
  blacklistedCategories: z.array(z.string()).optional(),
  location: z.string().optional(),
  radiusKm: z.number().optional(),
  pushRRule: z.string().optional(),
  pushLocalHour: z.number().optional(),
  pushLocalMinute: z.number().optional(),
  instant: z.boolean().optional(),
  ticketAlerts: z.string().optional(),
  minTasteMatch: z.number().optional(),
  maxEvents: z.number().optional(),
  blacklistedWords: z.array(z.string()).optional(),
  whitelistedWords: z.array(z.string()).optional(),
});

export const webApi = {
  getEvents: endpoint({
    input: aiQuery,
    output: z.array(publicEventEntrySchema),
    authRequired: true,
  }),
  getUserTasteDecisions: endpoint({
    input: z.object({
      userId: z.string().optional(),
    }),
    output: z.array(tasteDecisionSchema),
    authRequired: true,
  }),
  getUserSavedEvents: endpoint({
    input: z.object({
      userId: z.string().optional(),
    }),
    output: z.array(publicEventEntrySchema),
    authRequired: true,
  }),
  getEventById: endpoint({
    input: z.object({
      eventId: z.string(),
    }),
    output: publicEventEntrySchema.nullable(),
    authRequired: true,
  }),
  getEventPageData: endpoint({
    input: z.object({
      eventId: z.string(),
      language: z.enum(["en", "he"]).optional(),
    }),
    output: eventPageDataSchema.nullable(),
    authRequired: false,
  }),
  checkUserPairing: endpoint({
    input: z.object({
      email: z.string(),
    }),
    output: z.object({
      isPaired: z.boolean(),
    }),
    authRequired: true,
  }),
  getApiToken: endpoint({
    input: z.object({}),
    output: z.object({
      token: z.string(),
    }),
    authRequired: true,
  }),
  createSubscriptionCheckoutSession: endpoint({
    input: z.object({
      phone: z.string().optional(),
    }),
    output: z.object({
      checkoutUrl: z.string(),
    }),
    authRequired: true,
  }),
  recordTasteDecision: endpoint({
    input: z.object({
      eventId: z.string(),
      direction: z.enum(["like", "dislike"]),
    }),
    output: z.object({
      ok: z.boolean(),
    }),
    authRequired: true,
  }),
  getUserProfile: endpoint({
    input: z.object({}),
    output: z.object({
      userId: z.string(),
      email: z.string().optional(),
      location: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      coordinates: z.array(z.number()).nullable().optional(),
      phone: z.string().optional(),
      phoneVerified: z.boolean().optional(),
    }),
    authRequired: true,
  }),
  sendPhoneVerificationCode: endpoint({
    input: z.object({
      phone: z.string(),
    }),
    output: z.object({
      ok: z.boolean(),
    }),
    authRequired: true,
  }),
  verifyPhoneCode: endpoint({
    input: z.object({
      phone: z.string(),
      code: z.string(),
    }),
    output: z.object({
      ok: z.boolean(),
    }),
    authRequired: true,
  }),
  findCampaignEvent: endpoint({
    input: z.object({ eventIdOrLink: z.string() }),
    output: publicEventEntrySchema.nullable(),
    authRequired: true,
  }),
  getCampaignAudiencePreview: endpoint({
    input: z.object({
      ...campaignTargetingSchema,
      eventIdOrLink: z.string().optional(),
    }),
    output: z.object({ matchingUsers: z.number() }),
    authRequired: true,
  }),
  getPromotionBalance: endpoint({
    input: z.object({}),
    output: z.object({
      balance: z.number(),
      allocated: z.number(),
      available: z.number(),
    }),
    authRequired: true,
  }),
  createEventCampaign: endpoint({
    input: z.object({
      ...campaignTargetingSchema,
      eventIdOrLink: z.string(),
      numPeople: z.number().int().positive().describe(
        "Number of users to push the event to ($1 credit per user)",
      ),
      promotionLink: z.string().optional().describe(
        "Affiliate or ticket link attached to the campaign",
      ),
      marketingBudget: z.number().optional().describe(
        "Optional ongoing promotion budget in USD ($1 per unique click)",
      ),
    }),
    output: campaignOutcomeSchema,
    authRequired: true,
  }),
  getUserSubscriptions: endpoint({
    input: z.object({}),
    output: z.array(userSubscriptionSchema),
    authRequired: true,
  }),
  upsertUserSubscription: endpoint({
    input: upsertUserSubscriptionInputSchema,
    output: z.object({ id: z.string() }),
    authRequired: true,
  }),
  deleteUserSubscription: endpoint({
    input: z.object({ id: z.string() }),
    output: z.object({ ok: z.boolean() }),
    authRequired: true,
  }),
  getFriendsData: endpoint({
    input: z.object({}),
    output: z.object({
      userPhone: z.string().optional(),
      userName: z.string().optional(),
      friends: z.array(
        z.object({
          id: z.string(),
          phone: z.string(),
          name: z.string().optional(),
        }),
      ),
      incomingRequests: z.array(
        z.object({
          id: z.string(),
          fromPhone: z.string(),
          fromName: z.string().optional(),
          createdAt: z.number(),
        }),
      ),
      outgoingRequests: z.array(
        z.object({
          id: z.string(),
          toPhone: z.string(),
          createdAt: z.number(),
        }),
      ),
    }),
    authRequired: true,
  }),
  sendFriendRequest: endpoint({
    input: z.object({
      friendPhone: z.string(),
    }),
    output: z.object({
      ok: z.boolean(),
      message: z.string(),
    }),
    authRequired: true,
  }),
  respondFriendRequest: endpoint({
    input: z.object({
      requestId: z.string(),
      action: z.enum(["accept", "decline", "cancel"]),
    }),
    output: z.object({
      ok: z.boolean(),
      message: z.string(),
    }),
    authRequired: true,
  }),
  removeFriend: endpoint({
    input: z.object({
      friendPhone: z.string(),
    }),
    output: z.object({
      ok: z.boolean(),
      message: z.string(),
    }),
    authRequired: true,
  }),
  shareEventWithFriends: endpoint({
    input: z.object({
      eventId: z.string(),
      note: z.string().optional(),
    }),
    output: z.object({
      ok: z.boolean(),
      notifiedCount: z.number(),
      message: z.string(),
    }),
    authRequired: true,
  }),
  getFriendsEvents: endpoint({
    input: z.object({}),
    output: z.array(
      z.object({
        event: publicEventEntrySchema,
        interestedFriends: z.array(z.string()),
      }),
    ),
    authRequired: true,
  }),
} as const;
